import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { getTestIds } from 'seagull/utils/TestIds';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { isSystemVariable, makeVariablePattern, normalizeVariableLabel, VARIABLE_PRESENCE_PATTERN } from '../TemplateVariables.utils';
import { useTemplateVariables } from '../TemplateVariablesContext';
import styles from './VariableInlineRenderer.module.scss';
export const GetVariableInlineRendererTID = (parentTestId)=>getTestIds(parentTestId);
function buildSegments(text, resolvedValues) {
    const segments = [];
    let lastIndex = 0;
    for (const match of text.matchAll(makeVariablePattern())){
        const matchStart = match.index;
        if (matchStart > lastIndex) {
            segments.push({
                type: 'text',
                value: text.slice(lastIndex, matchStart)
            });
        }
        const label = match[1].trim();
        const resolved = resolvedValues === null || resolvedValues === void 0 ? void 0 : resolvedValues[normalizeVariableLabel(label)];
        if (resolved !== undefined) {
            segments.push({
                type: 'resolved',
                value: resolved
            });
        } else {
            segments.push({
                type: 'badge',
                label
            });
        }
        lastIndex = matchStart + match[0].length;
    }
    if (lastIndex < text.length) {
        segments.push({
            type: 'text',
            value: text.slice(lastIndex)
        });
    }
    return segments;
}
const BADGE_STATE_CLASS = {
    default: undefined,
    setup: styles.badgeSetup,
    error: styles.badgeError
};
function computeBadgeState(label, explicitState, showVariableErrors, unresolvedCustomVariables) {
    if (explicitState !== 'default') return explicitState;
    const normalized = normalizeVariableLabel(label);
    if (showVariableErrors && unresolvedCustomVariables.some((v)=>normalizeVariableLabel(v) === normalized)) {
        return 'error';
    }
    if (!isSystemVariable(label) && unresolvedCustomVariables.some((v)=>normalizeVariableLabel(v) === normalized)) {
        return 'setup';
    }
    return 'default';
}
export function VariableInlineRenderer(param) {
    let { text, resolvedValues, badgeState = 'default', testId } = param;
    const { VariableInlineRendererTestIds } = useMemo(()=>GetVariableInlineRendererTID(testId), [
        testId
    ]);
    const { TEMPLATE_VARIABLES } = useFeatureFlags();
    const { showVariableErrors, unresolvedCustomVariables } = useTemplateVariables();
    const containsVariables = useMemo(()=>VARIABLE_PRESENCE_PATTERN.test(text), [
        text
    ]);
    const segments = useMemo(()=>TEMPLATE_VARIABLES && containsVariables ? buildSegments(text, resolvedValues) : [], [
        TEMPLATE_VARIABLES,
        containsVariables,
        text,
        resolvedValues
    ]);
    if (!TEMPLATE_VARIABLES || !containsVariables) {
        return /*#__PURE__*/ _jsx("span", {
            "data-testid": VariableInlineRendererTestIds.Root(),
            children: text
        });
    }
    return /*#__PURE__*/ _jsx("span", {
        className: styles.container,
        "data-testid": VariableInlineRendererTestIds.Root(),
        children: segments.map((segment, index)=>{
            const key = "".concat(segment.type, "-").concat(index);
            switch(segment.type){
                case 'text':
                case 'resolved':
                    return /*#__PURE__*/ _jsx("span", {
                        children: segment.value
                    }, key);
                case 'badge':
                    {
                        const state = computeBadgeState(segment.label, badgeState, showVariableErrors, unresolvedCustomVariables);
                        return /*#__PURE__*/ _jsx("span", {
                            className: cn(styles.badge, BADGE_STATE_CLASS[state]),
                            "data-testid": VariableInlineRendererTestIds.Badge(),
                            children: segment.label
                        }, key);
                    }
            }
        })
    });
}
